import React, { useEffect, useState } from 'react';
import { getNoticeById, deleteNotice, sendNotice } from '../../api/services/noticeApi';
import { useNavigate, useParams } from 'react-router-dom';
import SettingsHeader from '../../ui/SettingsHeader';
import { Bell, Edit, Trash2, Calendar, Clock, MapPin, FileText, Send, ChevronLeft, Users, Loader, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../ui/ConfirmationModal';
import LoadingOverlay from '../../ui/LoadingOverlay';

const NoticeDetail = () => {
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const [confirmModal, setConfirmModal] = useState({ open: false, type: null });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    getNoticeById(id)
      .then(res => setNotice(res.data || res))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSend = () => {
    setConfirmModal({ open: true, type: 'send' });
  };

  const handleDelete = () => {
    setConfirmModal({ open: true, type: 'delete' });
  };

  const confirmAction = async () => {
    setIsProcessing(true);
    try {
      if (confirmModal.type === 'send') {
        await sendNotice(id);
        toast.success('Sent successfully!');
        const updated = await getNoticeById(id);
        const updatedData = updated.data || updated;
        setNotice(updatedData);
      } else if (confirmModal.type === 'delete') {
        await deleteNotice(id);
        toast.success('Deleted successfully');
        navigate('/notices');
        return;
      }
      setConfirmModal({ open: false, type: null });
    } catch (error) {
      toast.error(confirmModal.type === 'send' ? 'Send failed' : 'Delete failed');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <LoadingOverlay />;
  if (!notice) return <div className="p-4">Notice not found.</div>;

  return (
    <div className="animate-in fade-in duration-500 pb-10 relative">
      {isProcessing && (
        <LoadingOverlay 
           message={confirmModal.type === 'delete' ? "Deleting Record" : "Broadcasting Notice"} 
           subtext="Synchronizing changes with secure database..."
        />
      )}

      <SettingsHeader
        title={notice.title}
        description={`Posted on ${new Date(notice.createdAt).toLocaleDateString()}`}
        showBack={true}
        onBack={() => navigate('/notices')}
        icon={Bell}
        buttons={[
          ...(notice.status !== 'sent' ? [{
            label: "Send Now",
            icon: Send,
            onClick: handleSend,
            variant: "secondary"
          }] : []),
          ...(notice.status !== 'sent' ? [{
            label: "Edit",
            icon: Edit,
            onClick: () => navigate(`/notices/${id}/edit`),
            variant: "default"
          }] : []),
          {
            label: "Delete",
            icon: Trash2,
            onClick: handleDelete,
            variant: "danger"
          }
        ]}
      />

      {/* Sent Notice Warning Banner */}
      {notice.status === 'sent' && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-blue-900">This notice has been sent</p>
              <p className="text-xs text-blue-700 mt-0.5">This notice cannot be edited as it has already been broadcasted to recipients.</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Message Column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-6 md:p-8 space-y-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" style={{ color: 'var(--primary-color, #4f46e5)' }} /> Notice Content
                  </h4>
                  {notice.pinned && (
                    <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-black uppercase tracking-widest border border-amber-100">
                      <ChevronLeft className="h-3 w-3 rotate-45" /> Pinned
                    </span>
                  )}
                </div>
                
                <div className="text-gray-900 leading-relaxed whitespace-pre-wrap font-bold text-lg md:text-xl">
                  {notice.content || <span className="text-gray-300 italic font-normal text-base">No content provided for this notice.</span>}
                </div>

                {notice.summary && (
                  <div className="pt-6 border-t border-gray-50">
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-2">WhatsApp Summary</p>
                    <p className="text-sm font-medium text-gray-600 italic">"{notice.summary}"</p>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50/50 px-6 py-4 border-t border-gray-50 flex items-center justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                <span className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(notice.createdAt).toLocaleDateString()}</span>
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {new Date(notice.createdAt).toLocaleTimeString()}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  ID: <span className="text-gray-900">{id.substring(id.length - 8).toUpperCase()}</span>
                </span>
              </div>
            </div>

            {/* Attachments Section - Large if many, card style if few */}
            {notice.attachments && notice.attachments.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-6">Linked Documents ({notice.attachments.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {notice.attachments.map((att, idx) => (
                    <a 
                      key={idx} 
                      href={att.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center justify-between p-4 bg-gray-50 border border-gray-100 rounded-xl transition-all group"
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)';
                        e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--primary-color, #4f46e5) 30%, white)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                        e.currentTarget.style.borderColor = '';
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform" style={{ color: 'var(--primary-color, #4f46e5)' }}>
                          <FileText className="h-4 w-4" />
                        </div>
                        <span className="text-xs font-bold text-gray-700 truncate max-w-[150px]">{att.name || 'View Document'}</span>
                      </div>
                      <ChevronLeft className="h-4 w-4 text-gray-300 rotate-180 transition-colors" onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color, #4f46e5)'} onMouseOut={(e) => e.currentTarget.style.color = ''} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Metadata Column */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
              <div>
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-4">Notice Status</p>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                    notice.category === 'Message' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : ''
                  }`}>
                    {notice.category}
                  </span>
                  {notice.priority && (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                      notice.priority === 'High' ? 'bg-red-50 text-red-700 border-red-100' : 
                      'bg-amber-50 text-amber-700 border-amber-100'
                    }`}>
                      {notice.priority}
                    </span>
                  )}
                </div>
              </div>

              <div className="pt-6 border-t border-gray-50">
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-4">Distribution</p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl mt-1" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                      <Users className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase">Recipients</p>
                      <p className="text-sm font-bold text-gray-900">{notice.sentToSummary || 'All Organization'}</p>
                    </div>
                  </div>
                  
                  {notice.location && (
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600 mt-1">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase">Location</p>
                        <p className="text-sm font-bold text-gray-900">{notice.location}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {notice.scheduledDate && (
                <div className="pt-6 border-t border-gray-50">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-3">Event Date</p>
                  <p className="text-sm font-black p-3 rounded-xl border text-center" style={{ color: 'var(--primary-color, #4f46e5)', backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
                    {new Date(notice.scheduledDate).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Quick Actions Helper */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white shadow-xl shadow-gray-200">
               <p className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-4">Administrator Logs</p>
               <div className="space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="opacity-70">Current Status</span>
                    <span className={`font-black uppercase tracking-widest ${notice.status === 'sent' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {notice.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="opacity-70">Editable</span>
                    <span className={`font-black uppercase tracking-widest ${notice.status !== 'sent' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {notice.status !== 'sent' ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="opacity-70">WhatsApp</span>
                    <span className="font-black uppercase tracking-widest text-emerald-400">Enabled</span>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, type: null })}
        onConfirm={confirmAction}
        title={confirmModal.type === 'delete' ? "Delete Notice" : "Send Notice"}
        message={confirmModal.type === 'delete' ? "Are you sure you want to delete this notice? This action cannot be undone." : "Send this notice now? This might trigger WhatsApp messages to recipients."}
        confirmText={confirmModal.type === 'delete' ? "Delete" : "Send Now"}
        variant={confirmModal.type === 'delete' ? "danger" : "info"}
        isLoading={isProcessing}
      />
    </div>
  );
};

export default NoticeDetail;
